import React, { useState } from 'react';
import { Mail, Phone, MapPin, Clock, Send, MessageCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import siteConfig from '../config/siteConfig';

import { BACKEND_URL } from '../config/api';
const API = `${BACKEND_URL}/api`;

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: '',
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await fetch(`${API}/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        console.log('Success:', 'Message envoyé ! Nous vous répondrons rapidement.');
        setFormData({ name: '', email: '', phone: '', subject: '', message: '' });
      } else {
        throw new Error('Erreur');
      }
    } catch (error) {
      console.log('Error:', "Erreur lors de l'envoi");
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const contactMethods = [
    {
      icon: Phone,
      title: 'Téléphone',
      value: siteConfig.contact.phone,
      link: `tel:${siteConfig.contact.phoneFormatted}`,
      desc: 'Lun-Ven 8h-18h'
    },
    {
      icon: MessageCircle,
      title: 'WhatsApp',
      value: 'Discuter maintenant',
      link: `https://wa.me/${siteConfig.contact.whatsapp.replace('+', '')}`,
      desc: 'Réponse rapide'
    },
    {
      icon: Mail,
      title: 'Email',
      value: siteConfig.contact.email,
      link: `mailto:${siteConfig.contact.email}`,
      desc: 'Réponse sous 24h'
    },
    {
      icon: MapPin,
      title: 'Adresse',
      value: siteConfig.address.street,
      link: siteConfig.address.mapsUrl,
      desc: `${siteConfig.address.postalCode} ${siteConfig.address.city}`
    },
  ];

  return (
    <div className="min-h-screen py-12 bg-black">
      {/* Header */}
      <div className="max-w-[1400px] mx-auto px-4 md:px-12 mb-12">
        <div className="relative py-16 rounded-2xl md:rounded-3xl overflow-hidden bg-[#0a0a0a] border border-white/5">
          <div className="relative text-center">
            <h1 className="font-heading text-3xl md:text-5xl font-semibold text-white mb-4 tracking-tight">
              Contact
            </h1>
            <p className="text-white/60 text-base md:text-lg max-w-2xl mx-auto font-light">
              Une question ? Besoin d'un devis ? Notre équipe est à votre écoute
            </p>
          </div>
        </div>
      </div>

      {/* Contact Methods */}
      <section className="max-w-[1400px] mx-auto px-4 md:px-12 mb-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {contactMethods.map((method, i) => (
            <a
              key={i}
              href={method.link}
              target={method.link.startsWith('http') ? '_blank' : undefined}
              rel={method.link.startsWith('http') ? 'noopener noreferrer' : undefined}
              className="group bg-white/[0.02] border border-white/5 hover:border-white/10 rounded-xl p-6 transition-all"
              data-testid={`contact-${method.title.toLowerCase()}`}
            >
              <method.icon className="w-7 h-7 text-white/50 mb-4 group-hover:text-white transition-colors" />
              <h3 className="text-base font-medium text-white mb-1">{method.title}</h3>
              <p className="text-emerald-400 font-medium text-sm mb-1">{method.value}</p>
              <p className="text-xs text-white/50">{method.desc}</p>
            </a>
          ))}
        </div>
      </section>

      {/* Form and Info */}
      <div className="max-w-[1400px] mx-auto px-4 md:px-12">
        <div className="grid lg:grid-cols-2 gap-8">
          {/* Form */}
          <div className="bg-[#0a0a0a] border border-white/5 rounded-2xl p-6 md:p-8">
            <h2 className="text-xl font-semibold text-white mb-2">
              Envoyez-nous un message
            </h2>
            <p className="text-white/60 text-sm mb-6">
              Remplissez le formulaire ci-dessous et nous vous répondrons dans les plus brefs délais
            </p>

            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-white/60 text-xs uppercase tracking-wider">Nom complet *</Label>
                  <Input
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    placeholder="Jean Dupont"
                    className="bg-black border-white/10 text-white placeholder:text-white/50 focus:border-white/20"
                    data-testid="contact-name"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-white/60 text-xs uppercase tracking-wider">Email *</Label>
                  <Input
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    placeholder="jean@exemple.com"
                    className="bg-black border-white/10 text-white placeholder:text-white/50 focus:border-white/20"
                    data-testid="contact-email"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-white/60 text-xs uppercase tracking-wider">Téléphone</Label>
                  <Input
                    name="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder="06 12 34 56 78"
                    className="bg-black border-white/10 text-white placeholder:text-white/50 focus:border-white/20"
                    data-testid="contact-phone"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-white/60 text-xs uppercase tracking-wider">Sujet *</Label>
                  <Input
                    name="subject"
                    value={formData.subject}
                    onChange={handleChange}
                    required
                    placeholder="Demande de devis"
                    className="bg-black border-white/10 text-white placeholder:text-white/50 focus:border-white/20"
                    data-testid="contact-subject"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label className="text-white/60 text-xs uppercase tracking-wider">Message *</Label>
                <Textarea
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  rows={6}
                  placeholder="Décrivez votre demande..."
                  className="bg-black border-white/10 text-white placeholder:text-white/50 focus:border-white/20 resize-none"
                  data-testid="contact-message"
                />
              </div>

              <Button 
                type="submit"
                disabled={loading}
                className="w-full bg-white text-black hover:bg-white/90 h-12 text-base font-medium rounded-full"
                data-testid="contact-submit"
              >
                {loading ? (
                  'Envoi en cours...'
                ) : (
                  <>
                    <Send className="w-4 h-4 mr-2" />
                    Envoyer le message
                  </>
                )}
              </Button>
            </form>
          </div>

          {/* Info */}
          <div className="space-y-6">
            {/* Hours */}
            <div className="bg-[#0a0a0a] border border-white/5 rounded-xl p-6">
              <h3 className="text-base font-medium text-white mb-4 flex items-center gap-2">
                <Clock className="w-4 h-4 text-white/50" />
                Horaires d'ouverture
              </h3>
              <div className="space-y-3">
                <div className="flex justify-between text-sm">
                  <span className="text-white/60">Lundi - Vendredi</span>
                  <span className="text-white">8h00 - 18h00</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-white/60">Samedi</span>
                  <span className="text-white">9h00 - 12h00</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-white/60">Dimanche</span>
                  <span className="text-white/50">Fermé</span>
                </div>
              </div>
            </div>

            {/* WhatsApp CTA */}
            <div className="bg-green-500/5 border border-green-500/20 rounded-xl p-6">
              <h3 className="text-base font-medium text-white mb-2">
                Besoin d'une réponse rapide ?
              </h3>
              <p className="text-white/60 text-sm mb-4">
                Contactez-nous directement sur WhatsApp pour une réponse immédiate
              </p>
              <a 
                href={`https://wa.me/${siteConfig.contact.whatsapp.replace('+', '')}`}
                target="_blank" 
                rel="noopener noreferrer"
              >
                <Button className="bg-green-600 hover:bg-green-500 w-full rounded-full">
                  <MessageCircle className="w-4 h-4 mr-2" />
                  Ouvrir WhatsApp
                </Button>
              </a>
            </div>

            {/* Map */}
            <div className="rounded-xl overflow-hidden border border-white/5 h-[300px]">
              <iframe
                src={siteConfig.address.mapsEmbed}
                width="100%"
                height="100%"
                style={{ border: 0, filter: 'invert(90%) hue-rotate(180deg)' }}
                allowFullScreen=""
                loading="lazy"
                title="FGS Alarme Montpellier Montpellier"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContactPage;
